import React, { useState, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  LinearProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslations } from '../../constants/translations/CaseTranslation';

const DocumentUploadModal = ({ 
  open, 
  onClose, 
  onUpload, 
  officeColor = '#1976d2', 
  disabled = false,
  language = 'spanish'
}) => {
  const translations = useTranslations(language); // Usar el hook internamente
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB en bytes
  const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/jpg'
  ];

  const validateFile = (file) => {
    if (!file) return { valid: false, error: translations.noFileSelected };
    
    if (file.size > MAX_FILE_SIZE) {
      return { 
        valid: false, 
        error: `${translations.fileTooLarge} ${(file.size / (1024 * 1024)).toFixed(2)}MB` 
      };
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      return { 
        valid: false, 
        error: translations.fileTypeNotAllowed 
      };
    }

    return { valid: true, error: '' };
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError('');
    setSuccess(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const validFiles = [];
      const errors = [];

      files.forEach(file => {
        const validation = validateFile(file);
        if (validation.valid) {
          const isDuplicate = selectedFiles.some(
            existingFile => existingFile.name === file.name && existingFile.size === file.size
          );
          if (!isDuplicate) {
            validFiles.push(file);
          }
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });

      if (errors.length > 0) {
        setError(errors.join('\n'));
      }
      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
      }
    }
  };

  const handleFileSelect = (e) => {
    setError('');
    setSuccess(false);
    
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      const validFiles = [];
      const errors = [];

      files.forEach(file => {
        const validation = validateFile(file);
        if (validation.valid) {
          const isDuplicate = selectedFiles.some(
            existingFile => existingFile.name === file.name && existingFile.size === file.size
          );
          if (!isDuplicate) {
            validFiles.push(file);
          }
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });

      if (errors.length > 0) {
        setError(errors.join('\n'));
      }
      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !onUpload) return;

    setUploading(true);
    setError('');
    setUploadProgress({});

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      
      try {
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { status: 'uploading', progress: 0 }
        }));

        await onUpload(file);

        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { status: 'success', progress: 100 }
        }));
        
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { status: 'failed', progress: 0 }
        }));
        failedCount++;
      }
    }

    if (failedCount === 0) {
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSelectedFiles([]);
        setUploadProgress({});
        setSuccess(false);
      }, 1500);
    } else {
      setError(`${failedCount} archivo(s) no se pudieron subir`);
    }

    setUploading(false);
  };

  const handleRemoveFile = (fileToRemove) => {
    setSelectedFiles(prev => prev.filter(file => file !== fileToRemove));
    setError('');
    setSuccess(false);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFiles([]);
      setError('');
      setSuccess(false);
      setUploadProgress({});
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        {translations.uploadDocument}
        <IconButton
          onClick={handleClose} 
          disabled={uploading}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        {/* Área de drag & drop */}
        <div
          style={{
            border: dragActive ? `2px dashed ${officeColor}` : '2px dashed #e0e0e0',
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: dragActive ? `${officeColor}08` : '#fafafa',
            transition: 'all 0.3s ease',
            opacity: disabled ? 0.5 : 1,
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={!disabled ? handleBrowseClick : undefined}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.currentTarget.style.borderColor = officeColor;
              e.currentTarget.style.backgroundColor = `${officeColor}05`;
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled && !dragActive) {
              e.currentTarget.style.borderColor = '#e0e0e0';
              e.currentTarget.style.backgroundColor = '#fafafa';
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleFileSelect}
            disabled={disabled}
          />

          {/* Icono de upload */}
          <div
            style={{
              fontSize: '48px',
              color: dragActive ? officeColor : '#bdbdbd',
              marginBottom: '1rem'
            }}
          >
            📁
          </div>
          
          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>
            {dragActive 
              ? translations.dropFileHere
              : translations.dragAndDropFile
            }
          </h3>
          
          <p style={{ color: '#666', margin: '0 0 1rem 0' }}>
            {translations.or}{' '}
            <span
              style={{
                color: officeColor,
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              {translations.selectFromComputer}
            </span>
          </p>

          <small style={{ color: '#999' }}>
            {translations.allowedFilesInfo}
          </small>
        </div>

        {/* Archivos seleccionado */}
        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 2, maxHeight: '300px', overflowY: 'auto' }}>
            {selectedFiles.map((file, index) => {
              const progress = uploadProgress[file.name];
              
              return (
                <div
                  key={index}
                  style={{
                    marginBottom: '0.5rem',
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    border: `1px solid ${progress ? (progress.status === 'success' ? '#4caf50' : progress.status === 'failed' ? '#f44336' : officeColor) + '40' : officeColor + '20'}`,
                    borderRadius: '8px',
                    backgroundColor: progress?.status === 'uploading' ? `${officeColor}08` : 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{ fontSize: '24px', color: officeColor, marginTop: '4px' }}>
                    {progress?.status === 'success' ? '✅' : 
                    progress?.status === 'failed' ? '❌' : 
                    progress?.status === 'uploading' ? '⏳' : '📄'}
                  </div>
                  
                  <div style={{ flexGrow: 1 }}>
                    <div style={{ 
                      fontWeight: 'bold', 
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      {file.name}
                      {progress?.status === 'uploading' && (
                        <Chip
                          label="Subiendo..." 
                          size="small" 
                          sx={{ 
                            height: '20px',
                            fontSize: '0.7rem',
                            backgroundColor: officeColor,
                            color: 'white'
                          }} 
                        />
                      )}
                      {progress?.status === 'success' && (
                        <Chip 
                          label="Completado" 
                          size="small" 
                          sx={{ 
                            height: '20px',
                            fontSize: '0.7rem',
                            backgroundColor: '#4caf50',
                            color: 'white'
                          }} 
                        />
                      )}
                      {progress?.status === 'failed' && (
                        <Chip 
                          label="Error" 
                          size="small" 
                          sx={{ 
                            height: '20px',
                            fontSize: '0.7rem',
                            backgroundColor: '#f44336',
                            color: 'white'
                          }} 
                        />
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      {formatFileSize(file.size)}
                    </div>
                    
                    {/* Barra de progreso individual */}
                    {progress && progress.status === 'uploading' && (
                      <LinearProgress 
                        sx={{ 
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: officeColor,
                            borderRadius: 3
                          }
                        }}
                      />
                    )}
                    {progress && progress.status === 'success' && (
                      <LinearProgress 
                        variant="determinate"
                        value={100}
                        sx={{ 
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#4caf50',
                            borderRadius: 3
                          }
                        }}
                      />
                    )}
                    {progress && progress.status === 'failed' && (
                      <Box sx={{ 
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#ffebee',
                        border: '1px solid #f44336'
                      }} />
                    )}
                  </div>
                  
                  {!uploading && (
                    <button
                      onClick={() => handleRemoveFile(file)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#d32f2f',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '4px',
                        marginTop: '4px'
                      }}
                    >
                      ❌
                    </button>
                  )}
                </div>
              );
            })}
          </Box>
        )}

        {/* Resumen de progreso general */}
        {uploading && (
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            backgroundColor: `${officeColor}10`, 
            borderRadius: 2,
            border: `1px solid ${officeColor}40`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: `3px solid ${officeColor}`,
                  borderTopColor: 'transparent',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />
              <Typography variant="body2" fontWeight="bold" color={officeColor}>
                Subiendo archivos...
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                📤 Procesando: {Object.keys(uploadProgress).filter(k => uploadProgress[k].status === 'uploading').length}
              </Typography>
              <Typography variant="caption" color="success.main">
                ✅ Completados: {Object.keys(uploadProgress).filter(k => uploadProgress[k].status === 'success').length}
              </Typography>
              {Object.keys(uploadProgress).filter(k => uploadProgress[k].status === 'failed').length > 0 && (
                <Typography variant="caption" color="error.main">
                  ❌ Fallidos: {Object.keys(uploadProgress).filter(k => uploadProgress[k].status === 'failed').length}
                </Typography>
              )}
            </Box>
            
            {/* Barra de progreso general */}
            <Box sx={{ mt: 1 }}>
              <LinearProgress 
                variant="determinate"
                value={(Object.keys(uploadProgress).length / selectedFiles.length) * 100}
                sx={{ 
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: officeColor,
                    borderRadius: 4
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {Object.keys(uploadProgress).length} de {selectedFiles.length} archivos procesados
              </Typography>
            </Box>
          </Box>
        )}

        {/* Mensajes de error mejorados */}
        {error && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: '#ffebee',
              border: '2px solid #f44336',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <Box sx={{ fontSize: '20px' }}>⚠️</Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="bold" color="#c62828" sx={{ mb: 0.5 }}>
                  Algunos archivos no se pudieron subir
                </Typography>
                <Typography variant="caption" color="#c62828" sx={{ whiteSpace: 'pre-line' }}>
                  {error}
                </Typography>
              </Box>
            </Box>
            
            {Object.keys(uploadProgress).filter(k => uploadProgress[k].status === 'success').length > 0 && (
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                ✅ {Object.keys(uploadProgress).filter(k => uploadProgress[k].status === 'success').length} archivo(s) se subieron correctamente
              </Typography>
            )}
          </Box>
        )}

        {/* Mensaje de éxito mejorado */}
        {success && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: '#e8f5e9',
              border: '2px solid #4caf50',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box sx={{ fontSize: '32px' }}>🎉</Box>
            <Box>
              <Typography variant="body2" fontWeight="bold" color="#2e7d32">
                        ¡Todos los archivos se subieron correctamente!
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedFiles.length} archivo(s) procesado(s) exitosamente
                      </Typography>
                    </Box>
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                  onClick={handleClose}
                  disabled={uploading}
                  variant="outlined"
                >
                  {translations.close}
                </Button>
                {selectedFiles.length > 0 && (
                  <Button onClick={handleUpload} disabled={uploading || disabled} variant="contained" sx={{
                      backgroundColor: officeColor,
                      '&:hover': { backgroundColor: officeColor, opacity: 0.8 },
                      '&:disabled': { backgroundColor: '#cccccc' }
                    }}>
                    {uploading 
                      ? `${translations.uploading}... (${Object.keys(uploadProgress).length}/${selectedFiles.length})`
                      : `${translations.uploadFile} (${selectedFiles.length})`
                    }
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          );
};

export default DocumentUploadModal;